import { useState, useMemo } from "react";
import DOMPurify from "dompurify";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Users, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { DaySection } from "@/components/quote/DaySection";
import { groupItemsByDay } from "@/lib/dayGroupingUtils";
import { splitInfrastructureFees } from "@/lib/infrastructureFees";
import { InfrastructureFeesSection } from "@/components/quote/InfrastructureFeesSection";
import { OngoingProgrammesSection } from "@/components/quote/OngoingProgrammesSection";
import { splitOngoingProgrammes } from "@/lib/ongoingProgrammes";

import { formatCurrency } from "@/lib/currencyUtils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { findBestProductMatch } from "@/lib/productMatchingUtils";
import { fetchRoomRatesForItems } from "@/lib/roomRateEnrichment";
import type { ItineraryItem } from "@/components/quote/ActivityCard";
import { ParticipantOptionSelector } from "./ParticipantOptionSelector";
import { baseItemCost } from "@/lib/quoteChoices";
import { scaleItemsForPax, calculateItemTotalCost, hasAccommodationItems } from "@/lib/itineraryCostUtils";
import { AlertTriangle } from "lucide-react";
import { ProgrammeSections } from "@/components/quote/ProgrammeSections";
import { programmePeriod } from "@/lib/itinerarySections";

interface ItineraryPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itineraryItems: any[];
  lead: {
    id: string;
    title?: string | null;
    client_name?: string | null;
    start_date?: string | null;
    end_date?: string | null;
    pax_count?: number | null;
    destination?: string | null;
    programme_notes?: string | null;
    itinerary_layout?: string | null;
    itinerary_sections?: unknown;
  };
  totalCost: number;
  currency: string;
  onConfirmGenerate: () => void;
}

export function ItineraryPreviewDialog({
  open,
  onOpenChange,
  itineraryItems,
  lead,
  totalCost,
  currency,
  onConfirmGenerate,
}: ItineraryPreviewDialogProps) {
  // Fetch products and suppliers for image enrichment
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [selectedOptionQuantity, setSelectedOptionQuantity] = useState<number | null>(null);

  const { data: products } = useQuery({
    queryKey: ["products-for-preview"],
    queryFn: async () => {
      const { data } = await supabase
        .from("products")
        .select("id, product_name, hero_image_url, secondary_image_url, image_url_3, image_url_4");
      return data;
    },
    enabled: open,
  });

  const { data: suppliers } = useQuery({
    queryKey: ["suppliers-for-preview"],
    queryFn: async () => {
      const { data } = await supabase
        .from("suppliers")
        .select("id, name, hero_image_url, website, secondary_image_url, image_url_3, image_url_4");
      return data;
    },
    enabled: open,
  });

  const roomRateIds = useMemo(
    () =>
      Array.from(
        new Set(
          (itineraryItems || [])
            .map((i: any) => i?.room_rate_id)
            .filter((v: any): v is string => !!v)
        )
      ),
    [itineraryItems]
  );

  const { data: roomRatesMap } = useQuery({
    queryKey: ["room-rates-for-preview", roomRateIds.join(",")],
    queryFn: () => fetchRoomRatesForItems(itineraryItems as any),
    enabled: open && roomRateIds.length > 0,
  });

  const basePax = lead?.pax_count || null;
  const effectivePax = selectedOptionQuantity || basePax;

  const quotableItemsRaw = itineraryItems.filter(
    (item) => item.include_in_quote !== false && !(item as any).is_background_cost
  );

  // Apply pax scaling to the items so the preview matches what the quote will show
  const quotableItems = useMemo(
    () => scaleItemsForPax(quotableItemsRaw, basePax, effectivePax),
    [quotableItemsRaw, basePax, effectivePax]
  );

  const scaledTotal = useMemo(() => {
    if (!basePax || !effectivePax || basePax === effectivePax) return totalCost;
    return quotableItems.reduce(
      (sum: number, it: any) => sum + baseItemCost(it),
      0
    );
  }, [quotableItems, basePax, effectivePax, totalCost]);

  const showAccommodationWarning =
    !!basePax && !!effectivePax && basePax !== effectivePax && hasAccommodationItems(quotableItemsRaw);

  // Enrich items with products/suppliers data so ActivityCard can resolve images
  const enrichedItems: ItineraryItem[] = quotableItems.map((item) => {
    // If the item already has linked_product from the query join, use it
    const linkedProduct = item.linked_product;

    // Build the products property that ActivityCard expects
    let productData: { hero_image_url: string | null; product_name: string; [k: string]: any } | null = null;

    if (linkedProduct && linkedProduct.hero_image_url) {
      productData = {
        hero_image_url: linkedProduct.hero_image_url, secondary_image_url: (linkedProduct as any).secondary_image_url ?? null, image_url_3: (linkedProduct as any).image_url_3 ?? null, image_url_4: (linkedProduct as any).image_url_4 ?? null,
        product_name: linkedProduct.product_name,
      };
    } else if (item.product_id && products) {
      // Direct product_id lookup
      const matched = products.find((p) => p.id === item.product_id);
      if (matched) {
        productData = {
          hero_image_url: matched.hero_image_url, secondary_image_url: (matched as any).secondary_image_url ?? null, image_url_3: (matched as any).image_url_3 ?? null, image_url_4: (matched as any).image_url_4 ?? null,
          product_name: matched.product_name,
        };
      }
    }

    // Fuzzy match fallback using product matching utils
    if (!productData && products && item.item_type !== 'accommodation') {
      const normalizedTitle = (item.title || item.custom_title || "").trim().toLowerCase();
      const normalizedDesc = (item.description || item.custom_description || "").trim().toLowerCase();
      const match = findBestProductMatch(normalizedTitle, normalizedDesc, products);
      if (match) {
        productData = {
          hero_image_url: match.hero_image_url, secondary_image_url: (match as any).secondary_image_url ?? null, image_url_3: (match as any).image_url_3 ?? null, image_url_4: (match as any).image_url_4 ?? null,
          product_name: match.product_name,
        };
      }
    }

    // Supplier image fallback
    let supplierData: { hero_image_url: string | null; name: string; website: string | null } | null = null;
    if (item.supplier && suppliers) {
      const normalizedSupplier = (item.supplier || "").trim().toLowerCase();
      const matched = suppliers.find(
        (s) => (s.name || "").trim().toLowerCase() === normalizedSupplier
      );
      if (matched) {
        supplierData = {
          hero_image_url: matched.hero_image_url, secondary_image_url: (matched as any).secondary_image_url ?? null, image_url_3: (matched as any).image_url_3 ?? null, image_url_4: (matched as any).image_url_4 ?? null,
          name: matched.name,
          website: (matched as any).website ?? null,
        } as any;
      }
    }

    // Title-based supplier fallback: match item title against supplier names
    if (!supplierData && !productData && suppliers) {
      const normalizedTitle = (item.title || item.custom_title || "").trim().toLowerCase();
      const matched = suppliers.find(
        (s) => (s.name || "").trim().toLowerCase() === normalizedTitle
      );
      if (matched) {
        supplierData = {
          hero_image_url: matched.hero_image_url, secondary_image_url: (matched as any).secondary_image_url ?? null, image_url_3: (matched as any).image_url_3 ?? null, image_url_4: (matched as any).image_url_4 ?? null,
          name: matched.name,
          website: (matched as any).website ?? null,
        } as any;
      }
    }

    return {
      ...item,
      products: productData,
      suppliers: supplierData,
      room_rate:
        item.room_rate_id && roomRatesMap?.get(item.room_rate_id)
          ? roomRatesMap.get(item.room_rate_id)
          : null,
    };
  });

  const { infrastructure: infrastructureFees, dayItems: allDayItems } = splitInfrastructureFees(enrichedItems);
  const { ongoing: ongoingProgrammes, dayItems } = splitOngoingProgrammes(allDayItems);
  const dayGroups = groupItemsByDay(dayItems, lead.start_date);
  const sectionsMode = lead.itinerary_layout === "sections";


  const handleConfirm = () => {
    onOpenChange(false);
    onConfirmGenerate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b flex-shrink-0">
          <DialogTitle className="text-xl">Itinerary Preview</DialogTitle>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
            {lead.title && <span className="font-medium text-foreground">{lead.title}</span>}
            {lead.client_name && (
              <>
                <span>—</span>
                <span>{lead.client_name}</span>
              </>
            )}
            {lead.destination && <Badge variant="outline">{lead.destination}</Badge>}
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
            {lead.start_date && (
              <span className="flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" />
                {format(new Date(lead.start_date), "dd MMM yyyy")}
                {lead.end_date && ` – ${format(new Date(lead.end_date), "dd MMM yyyy")}`}
              </span>
            )}
            {lead.pax_count && (
              <span className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {selectedOptionQuantity || lead.pax_count} pax
              </span>
            )}
          </div>
          {lead.id && (
            <div className="mt-2">
              <ParticipantOptionSelector
                leadId={lead.id}
                selectedOptionId={selectedOptionId}
                onSelect={(id, qty) => {
                  setSelectedOptionId(id);
                  setSelectedOptionQuantity(qty);
                }}
                variant="chips"
              />
            </div>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {sectionsMode && programmePeriod(lead.start_date, lead.end_date) && (
            <div className="mb-6 border-y py-4">
              <p className="text-xs font-medium uppercase text-muted-foreground">Programme Period</p>
              <p className="text-lg font-semibold">{programmePeriod(lead.start_date, lead.end_date)}</p>
            </div>
          )}
          {lead.programme_notes && (
            <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
              {/^\s*</.test(lead.programme_notes) ? (
                <div
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(lead.programme_notes, {
                      USE_PROFILES: { html: true },
                    }),
                  }}
                />
              ) : (
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                  {lead.programme_notes}
                </div>
              )}
            </div>
          )}
          {dayGroups.length === 0 && infrastructureFees.length === 0 && ongoingProgrammes.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              No items included in the quote.
            </p>
          ) : (
            <div className="space-y-6">
              <InfrastructureFeesSection items={infrastructureFees} />
              {sectionsMode ? (
                <ProgrammeSections items={[...ongoingProgrammes, ...dayItems] as any} sections={lead.itinerary_sections} />
              ) : <>
              <OngoingProgrammesSection items={ongoingProgrammes} />
              {dayGroups.map((group) => (
                <DaySection
                  key={group.dayNumber}
                  dayNumber={group.dayNumber}
                  date={group.date}
                  items={group.items}
                />
              ))}
              </>}
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="space-y-2">
            {showAccommodationWarning && (
              <div className="flex items-start gap-2 text-xs bg-muted text-muted-foreground border border-border rounded p-2 max-w-md">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>Accommodation rooms aren't auto-adjusted for the new pax count — review manually if needed.</span>
              </div>
            )}
            <div className="text-lg font-semibold">
              Total: {formatCurrency(scaledTotal, currency)}
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close & Edit
            </Button>
            <Button onClick={handleConfirm}>
              Confirm & Generate Quote
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
